import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import Button from '../Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import CloseIcon from '@material-ui/icons/Close';
import TextField from '@material-ui/core/TextField';
import {
  listMemberDocuments,
  openMemberDocument,
  resignEnrollmentAndFlagMeter,
  getNoteTags,
  createNoteTag,
} from '../../actions/members';
import { fetchMemberNotesOnly, fetchEnrollmentFormData } from '../../actions/search';
import { validateZip } from '../../util/validations';

// only render DB-sourced document locations as links when they are http(s)
const safeHref = url => (/^https?:\/\//i.test(String(url || '')) ? String(url) : null);

// TDSP config regexes are operator-maintained; an invalid pattern must not
// throw out of the submit handler — treat it as "skip the check"
const safeRegexTest = (pattern, value) => {
  try {
    return new RegExp(pattern).test(value);
  } catch (e) {
    return true;
  }
};

const REVIEW_STATUS_APPROVE = 'approved';
const REVIEW_STATUS_RESIGN = 'resign_required';
const REVIEW_STATUS_REJECT = 'rejected';
// local UI sentinel only — on submit this still writes contract_review_status
// as REVIEW_STATUS_REJECT, per the ticket ("Contract Review status is set to
// rejected"), with cs_meter.status set to 'cancelled' instead
const REVIEW_STATUS_CANCEL = 'cancel_enrollment';

const CONTRACT_REVIEW_NOTE_TAG_NAME = 'Contract Review';

const REJECT_REASONS = [
  'Incomplete and/or blurry utility bill',
  'Additional customer information required',
  'Annual Usage Below 1000kWh',
  'Invalid rate class',
  'Customer already on Community Solar',
  'Account has been Final Billed',
  'Disconnect Notice on bill',
  'Utility bill over 6 months old',
];

const CANCEL_REASONS = [
  'Name mismatch',
  'Utility Account mismatch',
  'Incorrect Utility',
  'Customer has Rooftop Solar',
  'Customer requested cancellation',
  "Customer's account is inactive",
];

// cs_meter.cancellation_reason has a DB check constraint (cancellation_reason_cc)
// allowing only these snake_case values — the label is display-only
const CANCELLATION_REASON_OPTIONS = [
  { value: 'utility_inactive', label: 'Utility Inactive' },
  { value: 'customer_initiated', label: 'Customer Initiated' },
  { value: 'ineligible', label: 'Ineligible' },
];

const ANNUAL_VOLUME_MIN = 1000;
const ANNUAL_VOLUME_MAX = 40000;

// GX-3698 (2.b.ii): stored verbatim in cs_meter.utility_rate_code.
// NOTE: that column carries two value domains by design (ticket owner
// sign-off, GX-3698): contract review writes these rate-class labels, while
// the HU Fix Reject flow writes TDSP rate codes from tdsp_rate_codes.
const RATE_CODE_OPTIONS = ['Residential', 'Small Commercial', 'Large Commercial'];

const useStyles = makeStyles({
  root: {
    width: '550px',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    border: 'none',
  },
  menuBar: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '18px',
    fontWeight: 'bold',
    padding: '10px',
    backgroundColor: '#202124',
    color: 'white',
    letterSpacing: '1px',
    '& .MuiSvgIcon-root': {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#585858',
        color: 'white',
      },
    },
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '10px',
  },
  sectionHeader: {
    fontSize: '15px',
    fontWeight: 'bold',
    borderBottom: '1px solid black',
    paddingBottom: '3px',
  },
  memberIDHeader: {
    fontSize: '15px',
    fontWeight: 'bold',
  },
  documentList: {
    maxHeight: '150px',
    overflowY: 'auto',
    padding: 0,
  },
  fileLink: {
    background: 'none',
    border: 'none',
    color: '#1976d2',
    cursor: 'pointer',
    padding: 0,
    fontSize: 'inherit',
    textDecoration: 'underline',
    textAlign: 'left',
  },
  optionContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: '0px 10px',
    marginBottom: '10px',
  },
  errorMessage: {
    color: 'red',
    margin: '10px',
    marginTop: '0px',
  },
  warningBanner: {
    backgroundColor: '#fff3cd',
    color: '#856404',
    border: '1px solid #ffeeba',
    borderRadius: '4px',
    padding: '8px 10px',
    fontSize: '13px',
  },
  confirmDialog: {
    textAlign: 'center',
    '& .MuiTypography-root': {
      width: '100%',
    },
  },
});

const ContractReview = props => {
  const {
    meter,
    closePopup,
    handleChange,
    member,
    memberFiles,
    noteTags,
    tdsps,
    doListMemberFiles,
    doOpenMemberFile,
    doResignEnrollment,
    doGetNoteTags,
    doCreateNoteTag,
    doFetchMemberNotes,
    doFetchEnrollmentFormData,
  } = props;
  const classes = useStyles();

  const enrollment = member.enrollment || {};
  // GX-4079: callers outside MemberDetails (the contract-review queue) pass
  // the member ID explicitly since state.selectedMember may not be loaded
  const memberID = props.memberID || member.memberID;
  const products = member.products || [];

  const [reviewStatus, setReviewStatus] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState('');

  // resign-editable fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [meterIdentifier, setMeterIdentifier] = useState('');
  const [meterIdentifier2, setMeterIdentifier2] = useState('');
  const [annualVolume, setAnnualVolume] = useState('');
  const [rateCode, setRateCode] = useState('');
  const [enrollmentFormData, setEnrollmentFormData] = useState({});

  // GX-7626: company_name/title aren't on the EnrollmentInfo object fetched
  // above — only on FormData, fetched separately by enrollment id
  useEffect(() => {
    if (!enrollment.id) {
      setEnrollmentFormData({});
      return;
    }
    doFetchEnrollmentFormData(enrollment.id)
      .then(res => setEnrollmentFormData((res && res.formData) || {}))
      .catch(() => setEnrollmentFormData({}));
  }, [enrollment.id]);

  useEffect(() => {
    if (memberID) {
      doListMemberFiles(memberID);
      // the Contract Review queue entry point never loads state.selectedMember.notes —
      // fetch just the notes (not the whole member-info bundle, which caused a
      // render loop) so Contract Review Notes has data there too
      doFetchMemberNotes(memberID);
    }
    doGetNoteTags();
  }, [memberID]);

  // ensure the "Contract Review" note tag exists so notes can be filed under it.
  // noteTags starts as the reducer's default {} before the getNoteTags() fetch
  // above resolves — skip that first, pre-fetch render so we don't judge
  // existence against data that hasn't loaded yet, and only ever attempt the
  // create once per modal open (a second dispatch of getNoteTags happens
  // inside createNoteTag itself, which would otherwise re-trigger this effect)
  const isFirstNoteTagsRenderRef = useRef(true);
  const tagCreateAttemptedRef = useRef(false);
  useEffect(() => {
    if (isFirstNoteTagsRenderRef.current) {
      isFirstNoteTagsRenderRef.current = false;
      return;
    }
    if (tagCreateAttemptedRef.current || !noteTags) {
      return;
    }
    tagCreateAttemptedRef.current = true;
    if (!Object.values(noteTags).some(t => t.name === CONTRACT_REVIEW_NOTE_TAG_NAME)) {
      doCreateNoteTag({ name: CONTRACT_REVIEW_NOTE_TAG_NAME });
    }
  }, [noteTags]);

  useEffect(() => {
    const address = enrollment.address || {};
    setFirstName(enrollment.first_name || '');
    setLastName(enrollment.last_name || '');
    setLine1(address.line1 || '');
    setLine2(address.line2 || '');
    setCity(address.city || '');
    setPostalCode(address.postal_code || '');
    setMeterIdentifier(
      (meter && meter.get('meter_identifier')) || enrollment.meter_identifier || ''
    );
    setMeterIdentifier2(
      (meter && meter.get('meter_identifier2')) || enrollment.meter_identifier2 || ''
    );
    setAnnualVolume(meter && meter.get('annual_kwh') ? `${meter.get('annual_kwh')}` : '');
    // normalise at load: legacy TDSP rate codes are not in the option list —
    // show them as unset rather than silently carrying a hidden value
    const storedRateCode = meter ? meter.get('utility_rate_code') || '' : '';
    setRateCode(RATE_CODE_OPTIONS.includes(storedRateCode) ? storedRateCode : '');
    // member is a fresh toJSON() object every store update (and meter a fresh
    // Immutable Map every refetch) — key the reset on stable identifiers so
    // in-progress edits are not wiped by unrelated dispatches
  }, [enrollment.id, memberID, meter && meter.get('id')]);

  // GX-7013: the utility is fixed for the whole review — it is displayed but
  // never editable, so the meter's stored TDSP is the only one in play
  const currentTdsp = tdsps.find(t => t.duns_number === (meter ? meter.get('tdsp_duns') : ''));
  const meterIdConfig = currentTdsp && currentTdsp.config && currentTdsp.config.meterIdentifier;
  const meterId2Config = currentTdsp && currentTdsp.config && currentTdsp.config.meterIdentifier2;
  const meterIdLabel = (meterIdConfig && meterIdConfig.description) || 'Meter Identifier';
  const meterId2Label = (meterId2Config && meterId2Config.description) || 'Meter Identifier 2';
  // GX-7455: e-mail and phone are fixed for the whole review, like the utility —
  // displayed but never editable, and left off the resign payload so the
  // enrollment keeps its stored contact details
  const phone = enrollment.phone || member.phone || '';
  const email = enrollment.email || member.email || '';
  const canResign = !!enrollment.id;
  const isResign = reviewStatus === REVIEW_STATUS_RESIGN;

  // GX-7490: a meter rejected as a duplicate enrollment is locked outside contract
  // review — the backend freezes status/status_reason on any contract review
  // decision for it (see UpdateCSMeter's keepDuplicateRejected guard). This is
  // purely informational; every decision still submits and updates everything else.
  const isDuplicateRejected = !!meter &&
    meter.get('status') === 'rejected' &&
    `${meter.get('status_reason') || ''}`.trim().toLowerCase() === 'duplicate enrollment';

  const contractReviewTag = Object.values(noteTags || {})
    .find(t => t.name === CONTRACT_REVIEW_NOTE_TAG_NAME);
  const contractReviewNotes = contractReviewTag
    ? (member.notes || []).filter(
      // state.selectedMember.notes is a single shared slice — guard against
      // showing a previously-reviewed member's stale notes while this
      // member's fetch is still in flight (or failed)
      n => n.memberID === memberID &&
        (n.tags || []).some(tagID => String(tagID) === String(contractReviewTag.ID))
    )
    : [];

  // distinguish "no contracts" from "contract list failed to load" — a
  // reviewer must never approve because a broken list rendered as empty
  const { docs: productDocuments, failed: contractsFailed } = products.reduce(
    (acc, product) => {
      const raw = product.documents;
      if (Array.isArray(raw)) {
        return { docs: acc.docs.concat(raw), failed: acc.failed };
      }
      if (typeof raw === 'string' && raw) {
        try {
          return { docs: acc.docs.concat(JSON.parse(raw) || []), failed: acc.failed };
        } catch (e) {
          return { docs: acc.docs, failed: true };
        }
      }
      return acc;
    },
    { docs: [], failed: false }
  );

  // GX-3698 (2.b.iii): Annual Volume + Rate Code are required and valid for
  // Approve and Requires Resign; optional (but validated when set) for Reject
  const validateVolumeAndRateCode = (required) => {
    const volumeText = `${annualVolume}`.trim();
    if (required && !volumeText) {
      return 'Annual volume is required.';
    }
    if (volumeText) {
      const volume = Number(volumeText);
      if (!/^\d+$/.test(volumeText) ||
        volume < ANNUAL_VOLUME_MIN || volume > ANNUAL_VOLUME_MAX) {
        return `Annual volume must be an integer between ${ANNUAL_VOLUME_MIN} and ${ANNUAL_VOLUME_MAX} kWh.`;
      }
    }
    if (required && !RATE_CODE_OPTIONS.includes(rateCode)) {
      return 'Please select a rate code.';
    }
    return '';
  };

  const validateResign = () => {
    if (!firstName.trim() || !lastName.trim()) {
      return 'First and last name are required.';
    }
    if (!line1.trim() || !city.trim()) {
      return 'Service address is required.';
    }
    if (!validateZip(postalCode.trim())) {
      return 'A valid 5-digit zip code is required.';
    }
    // GX-7013: the utility can no longer be corrected here, so an unresolvable
    // one blocks the resign — reject with "Incorrect Utility" instead
    if (!currentTdsp) {
      return 'This meter has no valid utility on file, so it cannot be resigned. ' +
        'Cancel the enrollment with reason "Incorrect Utility" instead.';
    }
    const volumeAndRateError = validateVolumeAndRateCode(true);
    if (volumeAndRateError) {
      return volumeAndRateError;
    }
    if (!meterIdentifier.trim()) {
      return `${meterIdLabel} is required.`;
    }
    if (meterIdConfig && meterIdConfig.regex &&
      !safeRegexTest(meterIdConfig.regex, meterIdentifier.trim())) {
      return `Invalid ${meterIdLabel}.`;
    }
    if (meterId2Config) {
      if (!meterIdentifier2.trim()) {
        return `${meterId2Label} is required.`;
      }
      if (meterId2Config.regex &&
        !safeRegexTest(meterId2Config.regex, meterIdentifier2.trim())) {
        return `Invalid ${meterId2Label}.`;
      }
    }
    return '';
  };

  const submitForm = () => {
    setError('');
    if (reviewStatus === REVIEW_STATUS_APPROVE) {
      const validationError = validateVolumeAndRateCode(true);
      if (validationError) {
        setError(validationError);
        return;
      }
    }
    if (reviewStatus === REVIEW_STATUS_REJECT) {
      if (!rejectReason) {
        setError('Please select a reason for rejecting the review.');
        return;
      }
      const validationError = validateVolumeAndRateCode(false);
      if (validationError) {
        setError(validationError);
        return;
      }
    }
    if (reviewStatus === REVIEW_STATUS_CANCEL) {
      if (!cancelReason || !cancellationReason) {
        setError('Please select a Reason and a Cancellation Reason.');
        return;
      }
      const validationError = validateVolumeAndRateCode(false);
      if (validationError) {
        setError(validationError);
        return;
      }
    }
    if (isResign) {
      const validationError = validateResign();
      if (validationError) {
        setError(validationError);
        return;
      }
    }
    setConfirmOpen(true);
  };

  // GX-4079: await the action before closing so callers can refresh their
  // lists right after close and see the updated review state
  const handleConfirm = async () => {
    setConfirmOpen(false);
    // GX-3698 (2.b.iii.1): persist Annual Volume / Rate Code with the decision
    // GX-6990: usage_source rides along with the usage edit only, so a decision
    // recorded without touching Annual Volume leaves cs_meter.usage_source NULL
    const meterFields = {
      ...(`${annualVolume}`.trim()
        ? { annual_kwh: Number(annualVolume), usage_source: 'admin_portal' }
        : {}),
      ...(rateCode ? { utility_rate_code: rateCode } : {}),
    };
    if (reviewStatus === REVIEW_STATUS_APPROVE) {
      // GX-3698 (2.c.i.1): approval returns the meter to 'new' and clears the
      // stale reason. Guarded so an approval can't resurrect a meter in an
      // unrelated state (e.g. cancelled/active) — only new/rejected reset.
      const currentStatus = meter ? meter.get('status') : '';
      const resetStatus = ['new', 'rejected'].includes(currentStatus)
        ? { status: 'new', status_reason: '' }
        : {};
      await handleChange({
        contract_review_status: REVIEW_STATUS_APPROVE,
        ...resetStatus,
        ...meterFields,
      });
    } else if (reviewStatus === REVIEW_STATUS_REJECT) {
      await handleChange({
        contract_review_status: REVIEW_STATUS_REJECT,
        status: 'rejected',
        status_reason: rejectReason,
        ...meterFields,
      });
    } else if (reviewStatus === REVIEW_STATUS_CANCEL) {
      await handleChange({
        contract_review_status: REVIEW_STATUS_REJECT,
        status: 'cancelled',
        status_reason: cancelReason,
        cancellation_reason: cancellationReason,
        ...meterFields,
      });
    } else if (isResign) {
      await doResignEnrollment(
        {
          id: enrollment.id,
          formData: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            address: {
              ...(enrollment.address || {}),
              line1: line1.trim(),
              line2: line2.trim(),
              city: city.trim(),
              postal_code: postalCode.trim(),
            },
            meterUID: meterIdentifier.trim(),
            ...(meterId2Config ? { meterUID2: meterIdentifier2.trim() } : {}),
            tdspName: currentTdsp.name,
            tdspDuns: currentTdsp.duns_number,
            estimated_annual_usage: Number(annualVolume),
          },
        },
        meter.get('id'),
        memberID,
        meterFields
      );
    }
    closePopup();
  };

  const confirmText = () => {
    if (reviewStatus === REVIEW_STATUS_APPROVE) {
      return 'Are you sure you want to approve this contract review?';
    }
    if (reviewStatus === REVIEW_STATUS_REJECT) {
      return `Are you sure you want to reject this contract review? Reason: ${rejectReason}`;
    }
    if (reviewStatus === REVIEW_STATUS_CANCEL) {
      return `Are you sure you want to cancel this enrollment? Reason: ${cancelReason}`;
    }
    return 'Are you sure you want to send a new disclosure for signing?';
  };

  // GX-3698 (2.b.iv): fields are greyed out (disabled) when not editable —
  // matching the Enrollments-tab view/edit behaviour. Annual Volume and Rate
  // Code are always editable; the rest only under Requires Resign.
  const renderField = (label, value, onChange, alwaysEditable = false) => (
    <Grid item sm={6}>
      <TextField
        label={label}
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={!alwaysEditable && !isResign}
        variant='outlined'
        size='small'
        fullWidth
      />
    </Grid>
  );

  return (
    <div className={classes.root}>
      <Card className={classes.container} variant='outlined'>
        <div className={classes.menuBar}>
          <span>Review Contracts</span>
          <CloseIcon onClick={() => closePopup()} />
        </div>
        <div className={classes.form}>
          {memberID && (
            <div className={classes.memberIDHeader}>
              Member ID: <Link to={`/members/${memberID}/details`}>{memberID}</Link>
            </div>
          )}
          <div className={classes.sectionHeader}>Contracts</div>
          <List className={classes.documentList}>
            {contractsFailed && (
              <ListItem className={classes.errorMessage}>
                Could not load the contract list — do not approve without reviewing the contracts.
              </ListItem>
            )}
            {!contractsFailed && productDocuments.length === 0 && (
              <ListItem>No contracts found.</ListItem>
            )}
            {productDocuments.map((doc, i) => (
              <ListItem key={`doc_${i}`}>
                {safeHref(doc && doc.location) ? (
                  <a rel='noopener noreferrer' href={safeHref(doc.location)} target='_blank'>
                    {doc && doc.description}
                  </a>
                ) : (
                  <span>{doc && doc.description}</span>
                )}
              </ListItem>
            ))}
          </List>
          <div className={classes.sectionHeader}>Customer Files</div>
          <List className={classes.documentList}>
            {(!memberFiles || memberFiles.length === 0) && (
              <ListItem>No customer files found.</ListItem>
            )}
            {(memberFiles || []).map((file, i) => (
              <ListItem key={`file_${i}`}>
                <button
                  type='button'
                  className={classes.fileLink}
                  title={file.file_name}
                  onClick={() => doOpenMemberFile(memberID, file.fileID)}
                >
                  {file.file_category || file.file_name}
                </button>
              </ListItem>
            ))}
          </List>
          <div className={classes.sectionHeader}>Contract Review Notes</div>
          <List className={classes.documentList}>
            {contractReviewNotes.length === 0 && (
              <ListItem>No Contract Review notes found.</ListItem>
            )}
            {contractReviewNotes.map(note => (
              <ListItem key={`note_${note.ID}`}>{note.body}</ListItem>
            ))}
          </List>
          <div className={classes.sectionHeader}>Enrollment Details</div>
          <Grid container spacing={1}>
            {/* GX-3698 (2.b.i/ii): Annual Volume + Rate Code first, always editable */}
            {renderField(
              'Annual Volume (kWh)',
              annualVolume,
              // digits only at keystroke, matching MemberFixReject
              value => { if (/^\d*$/.test(value)) setAnnualVolume(value); },
              true
            )}
            <Grid item sm={6}>
              <TextField
                select
                label='Rate Code'
                value={RATE_CODE_OPTIONS.includes(rateCode) ? rateCode : ''}
                onChange={e => setRateCode(e.target.value)}
                variant='outlined'
                size='small'
                fullWidth
              >
                <MenuItem value="">&mdash;</MenuItem>
                {RATE_CODE_OPTIONS.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {/* GX-7495: a legacy/non-standard utility_rate_code (not one of the 3
                options above) is otherwise invisible — show the raw stored value
                here, read-only, regardless of whether it's one of the 3 options */}
            <Grid item sm={6}>
              <TextField
                label='Current Rate Code Value'
                value={(meter && meter.get('utility_rate_code')) || ''}
                disabled
                variant='outlined'
                size='small'
                fullWidth
              />
            </Grid>
            {/* GX-7626: display-only enrollment fields, not stored on cs_meter */}
            <Grid item sm={6}>
              <TextField
                label='Premise Type'
                value={enrollment.premise_type || ''}
                disabled
                variant='outlined'
                size='small'
                fullWidth
              />
            </Grid>
            <Grid item sm={6}>
              <TextField
                label='Company Name'
                value={enrollmentFormData.company_name || ''}
                disabled
                variant='outlined'
                size='small'
                fullWidth
              />
            </Grid>
            <Grid item sm={6}>
              <TextField
                label='Title'
                value={enrollmentFormData.title || ''}
                disabled
                variant='outlined'
                size='small'
                fullWidth
              />
            </Grid>
            {renderField('First Name', firstName, setFirstName)}
            {renderField('Last Name', lastName, setLastName)}
            {renderField('Service Address', line1, setLine1)}
            {renderField('Address Line 2', line2, setLine2)}
            {renderField('City', city, setCity)}
            {renderField('Zip Code', postalCode, setPostalCode)}
            {/* GX-7013: utility is display-only in every review scenario */}
            <Grid item sm={6}>
              <TextField
                label='Utility'
                value={(currentTdsp && currentTdsp.name) ||
                  (meter && meter.get('tdsp_duns')) || ''}
                disabled
                variant='outlined'
                size='small'
                fullWidth
              />
            </Grid>
            {renderField(meterIdLabel, meterIdentifier, setMeterIdentifier)}
            {meterId2Config &&
              renderField(meterId2Label, meterIdentifier2, setMeterIdentifier2)}
            {/* GX-7455: phone and e-mail are display-only in every review scenario */}
            <Grid item sm={6}>
              <TextField
                label='Phone Number'
                value={phone}
                disabled
                variant='outlined'
                size='small'
                fullWidth
              />
            </Grid>
            <Grid item sm={6}>
              <TextField
                label='E-mail Address'
                value={email}
                disabled
                variant='outlined'
                size='small'
                fullWidth
              />
            </Grid>
          </Grid>
          {isDuplicateRejected && (
            <div className={classes.warningBanner}>
              This meter was rejected as a Duplicate Enrollment. Status and Status
              Reason are locked and won't change from this review — use Cancel
              Enrollment on the member's Details page if this enrollment needs to
              be cancelled.
            </div>
          )}
          <div className={classes.sectionHeader}>Review Decision</div>
          <FormControl variant='outlined' size='small' fullWidth>
            <label>New Contract Review Status</label>
            <Select
              value={reviewStatus}
              onChange={e => {
                setError('');
                setReviewStatus(e.target.value);
              }}
            >
              <MenuItem value={REVIEW_STATUS_APPROVE}>Approve</MenuItem>
              <MenuItem value={REVIEW_STATUS_RESIGN} disabled={!canResign}>
                Requires Resign{canResign ? '' : ' (no enrollment record found)'}
              </MenuItem>
              <MenuItem value={REVIEW_STATUS_REJECT}>Reject</MenuItem>
              <MenuItem value={REVIEW_STATUS_CANCEL}>Cancel Enrollment</MenuItem>
            </Select>
          </FormControl>
          {reviewStatus === REVIEW_STATUS_REJECT && (
            <FormControl variant='outlined' size='small' fullWidth>
              <label>Reject Reason</label>
              <Select value={rejectReason} onChange={e => setRejectReason(e.target.value)}>
                {REJECT_REASONS.map(reason => (
                  <MenuItem key={reason} value={reason}>
                    {reason}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {reviewStatus === REVIEW_STATUS_CANCEL && (
            <>
              <FormControl variant='outlined' size='small' fullWidth>
                <label>Reason</label>
                <Select value={cancelReason} onChange={e => setCancelReason(e.target.value)}>
                  {CANCEL_REASONS.map(reason => (
                    <MenuItem key={reason} value={reason}>
                      {reason}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant='outlined' size='small' fullWidth>
                <label>Cancellation Reason</label>
                <Select
                  value={cancellationReason}
                  onChange={e => setCancellationReason(e.target.value)}
                >
                  {CANCELLATION_REASON_OPTIONS.map(reason => (
                    <MenuItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </div>
        {error && <div className={classes.errorMessage}>{error}</div>}
        <div className={classes.optionContainer}>
          <Button color='secondary' onClick={() => closePopup()}>
            Cancel
          </Button>
          {reviewStatus === REVIEW_STATUS_APPROVE && (
            <Button color='primary' onClick={submitForm}>
              Approve
            </Button>
          )}
          {reviewStatus === REVIEW_STATUS_REJECT && (
            <Button color='primary' onClick={submitForm}>
              Reject
            </Button>
          )}
          {reviewStatus === REVIEW_STATUS_CANCEL && (
            <Button color='primary' onClick={submitForm}>
              Cancel Enrollment
            </Button>
          )}
          {isResign && (
            <Button color='primary' onClick={submitForm}>
              Send New Disclosure
            </Button>
          )}
        </div>
      </Card>
      <Dialog
        onClose={() => setConfirmOpen(false)}
        aria-labelledby='customized-dialog-title'
        open={confirmOpen}
      >
        <DialogTitle className={classes.confirmDialog} id='customized-dialog-title'>
          <span>Please Confirm</span>
        </DialogTitle>
        <DialogContent dividers>{confirmText()}</DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setConfirmOpen(false)} color='secondary'>
            Close
          </Button>
          <Button autoFocus onClick={handleConfirm} color='primary'>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const mapStateToProps = state => ({
  member: state.selectedMember.toJSON(),
  memberFiles: state.memberDocuments.documents,
  noteTags: state.memberNoteTags.tags,
  tdsps: state.lookup.tdsps,
});

const mapDispatchToProps = dispatch => ({
  doListMemberFiles: memberID => dispatch(listMemberDocuments(memberID)),
  doOpenMemberFile: (memberID, fileID) => dispatch(openMemberDocument(memberID, fileID)),
  doResignEnrollment: (payload, meterID, memberID, meterFields) =>
    dispatch(resignEnrollmentAndFlagMeter(payload, meterID, memberID, meterFields)),
  doGetNoteTags: () => dispatch(getNoteTags()),
  doCreateNoteTag: tag => dispatch(createNoteTag(tag)),
  doFetchMemberNotes: memberID => dispatch(fetchMemberNotesOnly(memberID)),
  doFetchEnrollmentFormData: enrollmentID => dispatch(fetchEnrollmentFormData(enrollmentID)),
});

const ConnectedContractReview = connect(
  mapStateToProps,
  mapDispatchToProps
)(ContractReview);

export default ConnectedContractReview;
